import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { configurationCodes } from '@/configurations/constants';
import findResult, {
  findSelectedInCategory,
  countSelectedInCategory,
  isChecked,
  firstSelected,
} from '@/utils/resultsHelper';
import { requestUrl } from '@/constants/urls';
import { sortingFilters } from '@/constants/states';
import { readStates, saveStates, states } from '@/constants/lifecycle';
import { workspaceForms } from '@/workspaces/constants';
import { workspaceFormSections } from '@/workspaces/state/state';
import { datasetConditionsStatus } from '@/datasets/status/helper';
import { approvalStatus, requestSources } from '../constants';
import {
  isNewRequest,
  requestPollingStatus,
  withDictionariesTransferType,
  accessControlAsApproverOrRequester,
  requestAccessContext,
} from '../helper';
import {
  requestActionsState,
  newRequest,
  requestPanels,
  resolveRequestWorkflow,
  safeTransferType,
} from '../state/state';

const useRequestPanelContainerContent = ({ category, code }) => useSelector(({
  auth: { role: { permissions: { requests: { create: canCreate, read: canRead } } } },
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request, workflow, exists } = resolveRequestWorkflow(requests, workflows, { category, code }, {
    isDirty: false,
    name: '',
    ...readStates,
    ...saveStates,
  });

  return {
    exists,
    canCreate,
    canRead: exists && canRead,
    isDirty: request.isDirty,
    name: request.name,
    readErrors: request.readErrors,
    readStatus: request.readStatus,
    workflowReadStatus: workflow?.readStatus || states.NONE,
    saveErrors: request.saveErrors,
  };
});

const useRequestPanelContainer = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request, workflow, workflowKey } = resolveRequestWorkflow(
    requests,
    workflows,
    { category, code },
    { ...readStates },
  );

  return {
    readStatus: request.readStatus,
    workflowKey,
    workflowReadStatus: workflow?.readStatus || states.NONE,
  };
});

const useRequestPanel = ({ category, code, url }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request } = resolveRequestWorkflow(requests, workflows, { category, code }, { code });
  return { ...isNewRequest(request, url), requestCode: request.code };
});

const useRequestPanelContent = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request, workflow } = resolveRequestWorkflow(requests, workflows, { category, code }, {
    ...requestPanels(),
    transfer: { transfer_type: '' },
    request_source: requestSources.DATASET,
    status: approvalStatus.NONE,
  });

  return {
    panels: request.panels,
    sections: workflow?.sections || {},
    status: request.status,
  };
});

const useRequestSummaryList = ({ category, code }) => useSelector(({
  requests: { requests, allowPmnTransfer },
  workflows: { workflows },
}) => {
  const { request } = resolveRequestWorkflow(requests, workflows, { category, code }, {
    transfer: { transfer_type: '' },
    request_source: requestSources.DATASET,
    status: approvalStatus.NONE,
  });

  return {
    allowPmnTransfer,
    showDictionaries: withDictionariesTransferType(request.transfer?.transfer_type),
    showSystemCohort: request.request_source === requestSources.COHORT,
  };
});

const useRequestAccessWizard = ({ category, code }) => useSelector(({
  auth: { role: { permissions } },
  requests: { allowPmnTransfer, requests },
  workflows: { workflows },
  workspaces: { forms },
}) => {
  const { request, workflow } = resolveRequestWorkflow(requests, workflows, { category, code }, newRequest({ code }));
  const requestNewWorkspaceForm = findResult(forms, 'open', workspaceForms.REQUEST) || workspaceFormSections();

  return {
    activeStepIndex: request.steps.activeStepIndex,
    allowPmnTransfer,
    permissions,
    request,
    requestNewWorkspaceForm,
    workflow,
  };
});

const useCompleteActions = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => {
  const defaultSteps = { pollStatus: states.NONE, sourceUrl: '', subscriptionsReadStatus: states.NONE };
  const { request } = resolveRequestWorkflow(requests, workflows, { category, code }, {
    steps: defaultSteps, ...saveStates,
  });
  const { steps, saveStatus } = request;
  const { pollStatus, sourceUrl, subscriptionsReadStatus } = steps;

  return {
    pollStatus, saveStatus, sourceUrl, subscriptionsReadStatus,
  };
});

const useRequestStepInfo = ({ category, code, section }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => {
  const {
    request, workflow, workflowKey, source,
  } = resolveRequestWorkflow(requests, workflows, { category, code });
  const name = request.request_source === requestSources.COHORT ? source.name : source.catalogue?.title;

  return {
    fields: get(workflow, `sections.${section}.fields`, []),
    name,
    readErrors: workflow.readErrors,
    readStatus: workflow.readStatus,
    request,
    saveErrors: request.saveErrors,
    saveStatus: request.saveStatus,
    sourceCode: source.code,
    sourceType: request.request_source,
    values: request.fields,
    workflowKey,
  };
});

const useRequestDestinationAccordion = ({ category, code }) => useSelector(({
  auth: { currentUser },
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request } = resolveRequestWorkflow(requests, workflows, { category, code }, { requester: { uuid: '' } });
  return { request, withLink: request.requester.uuid === currentUser.id };
});

const useRequestConfirmation = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request } = resolveRequestWorkflow(requests, workflows, { category, code });
  return { polling: requestPollingStatus(request), saveStatus: request.saveStatus };
});

const useRequestConfirmationError = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request } = resolveRequestWorkflow(requests, workflows, { category, code });
  return { readErrors: request.readErrors, saveErrors: request.saveErrors, workflowError: request.error_message };
});

const useRequestWizardActions = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request, source } = resolveRequestWorkflow(
    requests,
    workflows,
    { category, code },
    newRequest({ category, code }),
  );
  const name = request.request_source === requestSources.COHORT ? source?.name : source?.catalogue?.title;
  return {
    activeStepIndex: request.steps.activeStepIndex,
    hasConditions: source ? datasetConditionsStatus(source).hasConditions : false,
    name,
    code: source?.code,
    entity: request.request_source,
  };
});

const useNewRequestCode = () => {
  const { newRequestCode } = useSelector(({ requests }) => requests);
  return { newRequestCode };
};

const useRequestActions = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request } = resolveRequestWorkflow(requests, workflows, { category, code }, { isNew: false });
  return { isNew: request.isNew };
});

const useRequestSummaryActions = ({ category, code }) => useSelector(({
  auth: { role, currentUser },
  requests: { requests },
  workflows: { workflows },
}) => {
  const { request } = resolveRequestWorkflow(requests, workflows, { category, code });
  return {
    ...accessControlAsApproverOrRequester(requestAccessContext(request, currentUser), role),
    readStatus: request ? request.readStatus : states.NONE,
    request,
    status: request ? request.status : approvalStatus.NONE,
    url: request ? requestUrl(request, '#') : '',
    userName: currentUser ? currentUser.name : null,
  };
});

const useReviewActions = ({ category }) => useSelector(({
  auth: {
    currentUser,
    role: {
      permissions: {
        requests: { destroy: allowArchive, read: allowRefresh },
        tasks: { read: allowTasks },
      },
    },
  },
  configurations: { configurations },
  requests: { requests },
}) => {
  const { readStatus } = requests[category];
  const selectedRequests = findSelectedInCategory(requests, category);
  const configuration = findResult(configurations, 'all', configurationCodes.WORKFLOW_TASK_LIST) || {
    value: { value: false },
  };

  return {
    allowArchive,
    allowRefresh,
    allowTasks,
    isRequester: selectedRequests.some(({ requester }) => currentUser.id === requester.uuid),
    readStatus,
    selectedCount: selectedRequests.length,
    taskLinkEnabled: !allowTasks && isChecked(configuration.value.value),
  };
});

const useRequestsActions = ({ category }) => useSelector(({
  auth: { role: { permissions: { requests: { create: allowCreate, destroy: allowDelete, read: allowRefresh } } } },
  requests: { requests, newRequestCode: code },
}) => {
  const { readStatus } = requests[category];
  const count = countSelectedInCategory(requests, category);

  return {
    allowCreate,
    allowDelete,
    allowRefresh,
    code,
    disableDelete: readStatus === states.PENDING || count === 0,
    readStatus,
  };
});

const useAccessLauncherMenu = () => useSelector(({
  auth: {
    role: {
      permissions: {
        requests: { create: allowCreate, read: allowRead, review: allowApprove },
        tasks: { read: allowTasks },
      },
    },
  },
  requests: { newRequestCode: code },
}) => ({
  allowApprove, allowCreate, allowRead, allowTasks, code,
}));

const useRequestDialogs = () => useSelector(({
  auth: { role: { permissions: { requests: { review, destroy }, transfers: { create } } } },
  requests: {
    approveRequestsOpen, archiveRequestOpen, deleteRequestOpen, transferRequestsOpen,
  },
}) => ({
  allowApprove: approveRequestsOpen && review,
  allowArchive: archiveRequestOpen && review,
  allowDelete: deleteRequestOpen && destroy,
  allowTransfer: transferRequestsOpen && create,
}));

const useProjectInfoActions = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
  workspaces: { locations, forms },
}) => {
  const { steps, readErrors, saveStatus } = requestActionsState(
    { category, code },
    requests,
    workflows,
    locations,
    forms,
  );
  const { sourceUrl } = steps;

  return { readErrors, saveStatus, sourceUrl };
});

const useAboutInfoActions = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
  workspaces: { locations, forms },
}) => {
  const {
    steps, request, saveStatus, workflow,
  } = requestActionsState({ category, code }, requests, workflows, locations, forms);
  const { sourceUrl, terms } = steps;

  return {
    request,
    saveStatus,
    sourceUrl,
    terms,
    workflow,
  };
});

const useSelectSourceActions = ({ category, code }) => useSelector(({
  requests: { requests },
  workflows: { workflows },
  workspaces: { locations, forms },
}) => {
  const { steps, readErrors, sources } = requestActionsState({ category, code }, requests, workflows, locations, forms);
  const { sourceUrl } = steps;

  return { readErrors, sources, sourceUrl };
});

const useDestinationInfoActions = ({ category, code }) => useSelector(({
  auth: { role: { permissions } },
  requests: { requests, allowPmnTransfer },
  workflows: { workflows },
  workspaces: { locations, forms },
}) => {
  const {
    steps,
    readErrors,
    request,
    requested,
    saveStatus,
  } = requestActionsState({ category, code }, requests, workflows, locations, forms);
  const { attachments, sourceUrl, workspaces } = steps;

  return {
    allowPmnTransfer,
    attachments,
    permissions,
    readErrors,
    request,
    requestNewWorkspace: request.workspace_requested,
    saveStatus,
    selectSource: sourceUrl === null,
    settingsReadStatus: requested.settingsReadStatus,
    sourceUrl,
    transferType: safeTransferType(request, requested, allowPmnTransfer),
    workspace: firstSelected(workspaces.results),
  };
});

const useRequestsCategoryState = ({ category }) => {
  const categoryState = useSelector(({ requests: { requests } }) => requests[category]);
  return categoryState || sortingFilters();
};

const useRequestsTableColumns = ({ category }) => {
  const { orderBy, singleSorting } = useRequestsCategoryState({ category });
  return { orderBy, singleSorting };
};

const useRequestsTableSettingsMenu = ({ category }) => {
  const { singleSorting } = useRequestsCategoryState({ category });
  return { singleSorting };
};

const useRequestsFilters = ({ category }) => {
  const {
    filter: filterTerm, approvedFlag, deniedFlag, errorFlag, pendingFlag, readErrors,
  } = useRequestsCategoryState({ category });
  const review = category === 'review';

  return {
    approvedFlag,
    deniedFlag,
    errorFlag: review ? false : errorFlag,
    filterTerm,
    pendingFlag,
    readErrors,
    review,
  };
};

export default {
  useAccessLauncherMenu,
  useCompleteActions,
  useNewRequestCode,
  useRequestAccessWizard,
  useRequestActions,
  useRequestConfirmation,
  useRequestConfirmationError,
  useRequestDestinationAccordion,
  useRequestDialogs,
  useRequestPanel,
  useRequestPanelContainer,
  useRequestPanelContainerContent,
  useRequestPanelContent,
  useRequestsActions,
  useRequestStepInfo,
  useRequestSummaryActions,
  useRequestSummaryList,
  useRequestWizardActions,
  useReviewActions,
  useDestinationInfoActions,
  useProjectInfoActions,
  useSelectSourceActions,
  useAboutInfoActions,
  useRequestsFilters,
  useRequestsTableColumns,
  useRequestsTableSettingsMenu,
};
